import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useEffect, useRef, useState } from 'react';
import { FaRegCalendarAlt, FaRegEye, FaUserEdit } from 'src/components/icon';
import MediaQuery from 'react-responsive';
import { APP_IMG, APP_URL, IS_NO_ROBOT_INDEX, IS_SHOW_COOKIE, SHOW_COMMEMORATE } from 'src/constants';
import { IMAGE_DEFAULT, WEB_DESC, WEB_KEYWORD, WEB_LOGO, WEB_NAME, WEB_TITLE, WEB_URL } from 'src/constants/layout';
import { DATALAYER_TYPE, sendDataLayerGlobal } from 'src/utils/datalayer/send-datalayer';

import isEmpty from 'lodash/isEmpty';
import { useRouter } from 'next/router';
import Script from 'next/script';
import StickyBox from 'react-sticky-box';
import { KEY_ADS_POSITION } from 'src/components/ads/dynamic-ads';
import CardH from 'src/components/cards/card-h';
import CardV from 'src/components/cards/card-v';
import { getAffiliate, getFeatureImage } from 'src/utils/contents';
import { setContents } from 'src/utils/contents/set-contents';
import { getCountviews } from 'src/utils/countview';
import { useDesktop } from 'src/utils/device';
import { escapeChar, isExistAds, timestamp, useDateUpdate, usePageViews } from 'src/utils/helper';
import styled from 'styled-components';
import { isShouldFetch } from 'src/utils/detect-scrolling';
const DynamicAds = dynamic(import('src/components/ads/dynamic-ads'));

const WidgetCookie = dynamic(import('src/components/widgets/widget-cookie'), { ssr: false });
const Carousels = dynamic(import('src/components/carousels'), { ssr: false });
const LineLink = dynamic(import('src/components/lineLink'));
const WidgetSocialShare = dynamic(import('src/components/widgets/widget-social-share'));
const AdsNative = dynamic(import('src/components/ads/ads-native'), { ssr: false });

const ReadFullVideo = ({ article, lastNews, related, articleSports, solrRelated, dataAffiliate, lineContent, ads, dataAdsNative }) => {
  const router = useRouter();
  const isDesktop = useDesktop();
  const _article = {
    ...article,
    title: article?.meta?.title || WEB_TITLE,
    description: article?.meta?.description || WEB_DESC,
    keyword: article?.meta?.keyword || WEB_KEYWORD,
    image_url: article?.meta?.og_social_image || IMAGE_DEFAULT
  };

  const [updateCountView, setUpdateCountView] = useState(0);
  const dateUpdate = useDateUpdate(_article?.published_at, true);
  const baseUrlCategory = `${_article?.canonical}`;

  const [sectionTrack, setSectionTrack] = useState('section-1');
  /* ----------------------------------- SEO ---------------------------------- */
  const articleTitleSeo = escapeChar(_article?.title) || WEB_TITLE;
  const articleDescriptionSeo = escapeChar(_article?.description) || WEB_DESC;
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    /* ------------------------------ GET COUNTVIEW ----------------------------- */
    (async () => {
      try {
        const _countviews = await getCountviews(_article?.id);
        if (_countviews) {
          setUpdateCountView(_countviews + Number(_article?.pageviews));
        }
      } catch (error) {
        setUpdateCountView(0);
      }
    })();
    setContents();
  }, []);

  // ACCEPT COOKIE ********************************
  const handleScrollAdsPostArticle = () => {
    const sectionEl = document.getElementById('cookie');
    const sticky = document.querySelector('.sticky-cookie');
    const cookies = document.querySelector('.displayC');
    if (sectionEl !== null) {
      if (sectionEl.offsetTop < document.documentElement.scrollTop + 400) {
        if (sticky) {
          sticky.style.display = 'block';
        }
        if (IS_SHOW_COOKIE === 'true') {
          cookies.style.display = 'none';
        }
      } else {
        if (sticky) {
          sticky.style.display = 'none';
        }
        if (IS_SHOW_COOKIE === 'true') {
          cookies.style.display = 'unset';
        }
      }
    }
  };

  useEffect(() => {
    document.addEventListener('scroll', handleScrollAdsPostArticle);
    return () => document.removeEventListener('scroll', handleScrollAdsPostArticle);
  });

  /* -------------------------------- DATALAYER ------------------------------- */

  const nextSectionIndexRef = useRef(0);
  const sectionsVisibleRef = useRef([]);
  const sectionRef = useRef(null);
  const footerRef = useRef(false);
  const loadMoreArticles = () => {
    try {
      if (nextSectionIndexRef?.current < related.length) {
        if (isShouldFetch()) {
          const currentIndex = nextSectionIndexRef.current;
          const section = related[currentIndex];

          nextSectionIndexRef.current = currentIndex + 1;
          sectionsVisibleRef.current = [...sectionsVisibleRef.current, section];

          setTimeout(() => {
            loadMoreArticles();
          }, 1000);
        } else {
          document.addEventListener('scroll', handleScroll);
        }
      }
    } catch (err) {
      console.error(`${timestamp()}==========> INDEX ERROR : ${nextSectionIndexRef.current}`, err);
    }
  };

  const handleParagraphOne = async () => {
    const sectionEl = await document.getElementById('paragraph-1');

    if (sectionEl) {
      if (sectionEl.offsetTop < document.documentElement.scrollTop) {
        sectionRef.current = 'paragraph-1';

        document.removeEventListener('scroll', handleParagraphOne);
        await sendDataLayerGlobal({
          type: DATALAYER_TYPE.TRACK_SCROLL,
          router: router?.pathname,
          section: 'scroll',
          data: {
            title: router.asPath,
            heading: 'paragraph-1'
          }
        });
      }
    }
  };

  const handleParagraphTwo = async () => {
    const sectionEl = await document.getElementById('paragraph-2');

    if (sectionEl) {
      if (sectionEl.offsetTop < document.documentElement.scrollTop) {
        sectionRef.current = 'paragraph-2';
        document.removeEventListener('scroll', handleParagraphTwo);
        await sendDataLayerGlobal({
          type: DATALAYER_TYPE.TRACK_SCROLL,
          router: router?.pathname,
          section: 'scroll',
          data: {
            title: router.asPath,
            heading: 'paragraph-2'
          }
        });
      }
    }
  };

  const handleParagraphThree = async () => {
    const sectionEl = await document.getElementById('paragraph-3');
    if (sectionEl) {
      if (sectionEl.offsetTop < document.documentElement.scrollTop) {
        sectionRef.current = 'paragraph-3';
        document.removeEventListener('scroll', handleParagraphThree);
        await sendDataLayerGlobal({
          type: DATALAYER_TYPE.TRACK_SCROLL,
          router: router?.pathname,
          section: 'scroll',
          data: {
            title: router.asPath,
            heading: 'paragraph-3'
          }
        });
      }
    }
  };

  const handleScrollToReadComplete = async () => {
    try {
      const sectionEl = await document.getElementById('add-line');
      if (sectionEl !== null) {
        if (sectionEl.offsetTop < document.documentElement.scrollTop) {
          // console.log(sectionEl);
          // console.log(localStorage.getItem('_t'), isNaN(localStorage.getItem('_t')));
          sectionRef.current = 'add-line';
          const _t = localStorage.getItem('_t');
          if (!isNaN(_t)) {
            const counter = parseInt(_t);
            const toArray = num => num && [...`${num}`].map(elem => parseInt(elem));
            const _a = toArray(counter)[toArray(counter).length - 1] === 0 ? 0 : 1;
            const _b = counter - 1;
            const start = Math.floor((_a === 0 ? _b : counter) / 10) * 10;
            const end = Math.ceil(counter / 10) * 10;
            const first = start === 0 ? 1 : start + 1;
            const second = end === 0 ? 10 : end;
            const timer = `${first}-${second}`;

            document.removeEventListener('scroll', handleScrollToReadComplete);
            await sendDataLayerGlobal({
              type: DATALAYER_TYPE.TRACK_READTIME,
              router: router?.pathname,
              section: 'scroll',
              data: {
                title: router.asPath,
                heading: 'read-complete',
                time: timer
              }
            });
            await localStorage.removeItem('_t');
          } else {
            document.removeEventListener('scroll', handleScrollToReadComplete);
            await sendDataLayerGlobal({
              type: DATALAYER_TYPE.TRACK_READTIME,
              router: router?.pathname,
              section: 'scroll',
              data: {
                title: router.asPath,
                heading: 'read-complete',
                time: '1-10'
              }
            });
            await localStorage.removeItem('_t');
          }
        }
      }
    } catch (err) {
      console.error(`${timestamp()} ==========> handleScrollToReadComplete ERROR`);
    } finally {
      footerRef.current = true;
    }
  };

  const handleScrollToSectionAds = async () => {
    const sectionEl = await document.getElementById('section-ads');
    if (sectionEl !== null) {
      if (sectionEl.offsetTop < document.documentElement.scrollTop) {
        sectionRef.current = 'section-ads';
        document.removeEventListener('scroll', handleScrollToSectionAds);
        await sendDataLayerGlobal({
          type: DATALAYER_TYPE.TRACK_SCROLL,
          router: router?.pathname,
          section: 'scroll',
          data: {
            title: router.asPath,
            heading: 'section-ads'
          }
        });
        footerRef.current = true;
      }
    }
  };
  const handleScrollToSectionFooter = async () => {
    const sectionEl = await document.getElementById('footer-wrapper');
    if (footerRef.current) {
      if (sectionEl?.offsetTop < document.documentElement.scrollTop + 500) {
        document.removeEventListener('scroll', handleScrollToSectionFooter);
        await sendDataLayerGlobal({
          type: DATALAYER_TYPE.TRACK_SCROLL,
          router: router?.pathname,
          section: 'scroll',
          data: {
            title: router.asPath,
            heading: 'footer-wrapper'
          }
        });
      }
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem('_t', '0');
      const increment = () => {
        const count = localStorage.getItem('_t');
        const time = parseInt(count);
        localStorage.setItem('_t', time + 1);
      };
      setInterval(increment, 1000);
    } catch (err) {
      // PROVIDE FEEDBACK TO THE USER
      console.log('Error: Please enable website cookies. ' + err);
    }
  }, []);

  const handleScroll = () => {
    if (isShouldFetch()) {
      document.removeEventListener('scroll', handleScroll);
      loadMoreArticles();
    }
  };

  useEffect(() => {
    document.addEventListener('scroll', handleScroll);
    return () => document.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', handleParagraphOne);
    return () => document.removeEventListener('scroll', handleParagraphOne);
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', handleParagraphTwo);
    return () => document.removeEventListener('scroll', handleParagraphTwo);
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', handleParagraphThree);
    return () => document.removeEventListener('scroll', handleParagraphThree);
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', handleScrollToReadComplete);
    return () => document.removeEventListener('scroll', handleScrollToReadComplete);
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', handleScrollToSectionAds);
    return () => document.removeEventListener('scroll', handleScrollToSectionAds);
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', handleScrollToSectionFooter);
    return () => document.removeEventListener('scroll', handleScrollToSectionFooter);
  }, []);
  /* -------------------------------------------------------------------------- */

  return (
    <>
      {/* -------------------------------- NEXT_SEO -------------------------------- */}
      <NextSeo
        noindex={IS_NO_ROBOT_INDEX}
        nofollow={IS_NO_ROBOT_INDEX}
        title={_article?.title ? `${_article?.title} - ขอบสนาม` : WEB_TITLE}
        description={_article?.description || WEB_TITLE}
        robotsProps={{
          maxImagePreview: 'large',
          maxSnippet: -1,
          maxVideoPreview: -1
        }}
        openGraph={{
          type: 'article',
          locale: 'th_TH',
          site_name: WEB_NAME,
          title: _article?.title ? `${_article?.title} - ขอบสนาม` : WEB_TITLE,
          description: _article?.description || WEB_TITLE,
          url: baseUrlCategory,
          article: {
            publishedTime: _article?.published_at,
            modifiedTime: _article?.updated_at,
            expirationTime: null,
            section: _article?.section?.th,
            authors: [`${WEB_URL.FACEBOOK}`],
            tags: _article?.tags
          },
          images: [
            {
              url: _article?.image_url,
              width: '700',
              height: '395',
              alt: _article?.title ? `${_article?.title} - ขอบสนาม` : WEB_TITLE
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: _article?.keyword || WEB_KEYWORD
          }
        ]}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={baseUrlCategory} />
        <meta property='twitter:title' content={_article?.title ? `${_article?.title} - ขอบสนาม` : WEB_TITLE} />
        <meta property='twitter:description' content={_article?.description || WEB_TITLE} />
        <meta property='twitter:image' content={_article?.image_url || IMAGE_DEFAULT} />

        {/* ------------------------------- Truehit -------------------------------- */}
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
                    __th_page="${_article?.section?.en}";
                  `
          }}
        />

        {/* ---------------------------- SNIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                    {
                      "@context": "https://schema.org",
                      "@type": "WebSite",
                      "name": "${WEB_NAME}",
                      "url": "${WEB_URL.WEB}",
                      "description": "${articleDescriptionSeo}",
                      "sameAs": [
                        "${WEB_URL.FACEBOOK}",
                        "${WEB_URL.YOUTUBE}",
                        "${WEB_URL.INSTAGRAM}"
                      ],
                      "potentialAction": {
                        "@type": "SearchAction",
                        "target": "${WEB_URL.WEB}search?q={search_term_string}",
                        "query-input": "required name=search_term_string"
                      }
                    }
                  `
          }}
        />

        {/* ---------------------------- SNIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: ` 
                    { 
                      "@context": "http://schema.org", 
                      "@type": "WebPage", 
                      "name": "${articleTitleSeo} - ขอบสนาม", 
                      "description": "${articleDescriptionSeo}", 
                      "publisher": { 
                        "@type": "ProfilePage", 
                        "name": "${WEB_NAME}" 
                      } 
                    } 
                  `
          }}
        />

        {/* ------------------------ SNIPPET : BreadcrumbList ------------------------ */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: ` 
                    { 
                      "@context": "http://schema.org", 
                      "@type": "BreadcrumbList", 
                      "itemListElement": [ 
                        { 
                          "@type": "ListItem", 
                          "position": 1, 
                          "name": "หน้าแรก", 
                          "item": "${APP_URL}" 
                        }, 
                        { 
                          "@type": "ListItem", 
                          "position": 2, 
                          "name": "${_article?.section?.th}", 
                          "item": "${APP_URL}/${_article?.section?.en}" 
                        }, 
                        { 
                          "@type": "ListItem", 
                          "position": 3, 
                          "name": "${articleTitleSeo} - ขอบสนาม", 
                          "item": "${_article?.canonical || `${APP_URL}${_article?.link}`}"   
                        } 
                      ] 
                    } 
                  `
          }}
        />

        {/* -------------------------- SNIPPET : NewsArticle ------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: ` 
                    { 
                      "@context": "http://schema.org", 
                      "@type": "NewsArticle", 
                      "mainEntityOfPage": { 
                        "@type": "WebPage", 
                        "@id": "${_article?.canonical || `${APP_URL}${_article?.link}`}"  
                      }, 
                      "headline": "${articleTitleSeo} - ขอบสนาม", 
                      "alternativeHeadline": "${articleTitleSeo} - ขอบสนาม", 
                      "description": "${articleDescriptionSeo}", 
                      "image": "${APP_IMG}${_article?.image}", 
                      "datePublished": "${_article?.published_at}",
                      "dateModified": "${_article?.updated_at}",
                      "author": { 
                        "@type": "Thing", 
                        "name": "${_article.byline || WEB_NAME}",
                        "url": "${APP_URL}"
                      }, 
                      "publisher": { 
                        "@type": "Organization", 
                        "name": "${WEB_NAME}", 
                        "logo": { 
                          "@type": "ImageObject", 
                          "url": "${APP_IMG}/images/${WEB_LOGO}" 
                        } 
                      } 
                    } 
                  `
          }}
        />

        {/* -------------------------------- ADS : PPN ------------------------------- */}
        {/* <script async type='text/javascript' dangerouslySetInnerHTML={{
            __html: `
            window.googletag = window.googletag || {cmd: []};
            googletag.cmd.push(function() {
              googletag.defineSlot('/12270527/Khobsanam_P4', [[300, 250]], 'div-gpt-ad-1585559986012-0').addService(googletag.pubads());
              googletag.defineSlot('/12270527/Khobsanam_R1', [[300, 250]], 'div-gpt-ad-1585560025838-0').addService(googletag.pubads());
              googletag.defineSlot('/12270527/Khobsanam_R2', [[300, 250]], 'div-gpt-ad-1585560068253-0').addService(googletag.pubads());
              googletag.defineSlot('/12270527/Khobsanam_P2', [[728, 90]], 'div-gpt-ad-1576746769475-0').addService(googletag.pubads());
              googletag.defineSlot('/12270527/Khobsanam_M2', [[300, 250]], 'div-gpt-ad-1576746695675-0').addService(googletag.pubads());
              googletag.defineSlot('/12270527/Khobsanam_M4', [[300, 250]], 'div-gpt-ad-1585563248778-0').addService(googletag.pubads());
              googletag.defineSlot('/12270527/Khobsanam_M5', [[300, 250]], 'div-gpt-ad-1585563274426-0').addService(googletag.pubads());

              googletag.pubads().enableSingleRequest();
              googletag.pubads().collapseEmptyDivs();
              googletag.enableServices();
            });
          `
          }} /> */}
        {/* -------------------------------- ADS : Native ------------------------------- */}
        {/* <script async type='text/javascript' src='//code.th.giraff.io/data/widget-khobsanamcom.js' /> */}
        {/* <script async src='https://www.tiktok.com/embed.js' /> */}
      </Head>

      {/* ------------------------------ MAIN : START ------------------------------ */}
      <DetailWrapper style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : '' }}>
        {/* --------------------------- Social embed script -------------------------- */}
        <Script src='//www.instagram.com/embed.js' strategy='lazyOnload' />
        <Script src='https://platform.twitter.com/widgets.js' strategy='lazyOnload' />
        <div className='container'>
          {/* ----------------------------------- CONTENTS : Start ---------------------------------- */}
          <div className='article-area'>
            <div className='row'>
              {/* ----------------------------- ADS : BILLBOARD ---------------------------- */}
              {_article.is_show_ads && isExistAds(ads, KEY_ADS_POSITION.BILLBOARD) && (
                <div className='overflow-hidden default-billboard mx-30'>
                  <DynamicAds position={KEY_ADS_POSITION.BILLBOARD} data={ads} />
                </div>
              )}
              <div className='col-12 col-lg-8 position-relative position-md-unset'>
                {/* ------------------------------ HEADING : START ? TYPE VEDIO !NULL ----------------------------- */}
                <div className='breadcrumb-area-video'>
                  <div className='breadcrumb-title-video'>
                    <h1 className='category'>{article?.title}</h1>
                  </div>
                </div>
                <div className='post-info'>
                  <ul className='dateUpdate'>
                    <li>
                      <FaRegCalendarAlt />
                      <span>{dateUpdate}</span>
                    </li>
                    <li>
                      <FaRegEye />
                      <span>{usePageViews(updateCountView)}</span>
                    </li>
                  </ul>
                  <div className='byline'>
                    {article?.byline && (
                      <>
                        <FaUserEdit /> <span> {article?.byline}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className='content-feature-image-video'>{getFeatureImage(article)}</div>
                <div></div>
                {/* ------------------------------ HEADING : END ? TYPE VEDIO !NULL ----------------------------- */}

                <div id='contents' className='article-content'>
                  {/* ----------------------------- ADS : INARTICLE - 1 ---------------------------- */}
                  {_article?.content_I && <>{_article?.is_show_ads && isExistAds(ads, KEY_ADS_POSITION.INARTICLE_1) && <DynamicAds position={KEY_ADS_POSITION.INARTICLE_1} data={ads} />}</>}
                  {article?.blurb && <h2 className='content-blurb'>{article.blurb}</h2>}
                  <div>{/* ------------------------------ HEADING : END ? TYPE VEDIO !NULL ----------------------------- */}</div>

                  {/* ------------------------- CONTENTS I and ADS : INARTICLE 1 ------------------------- */}
                  {_article?.content_I && (
                    <div id='paragraph-1' className='default-inread'>
                      <div className='content-detail' dangerouslySetInnerHTML={{ __html: _article?.content_I }} />
                      {_article?.is_show_ads && getAffiliate(1, dataAffiliate)}
                    </div>
                  )}

                  {/* ------------------------- CONTENTS II and ADS : INARTICLE 2 ------------------------ */}
                  {_article?.content_II && (
                    <div id='paragraph-2' className='default-inread'>
                      {_article?.is_show_ads && isExistAds(ads, KEY_ADS_POSITION.INARTICLE_2) && <DynamicAds position={KEY_ADS_POSITION.INARTICLE_2} data={ads} />}
                      <div className='content-detail' dangerouslySetInnerHTML={{ __html: _article?.content_II }} />
                      {_article?.is_show_ads && getAffiliate(2, dataAffiliate)}
                    </div>
                  )}

                  {/* -------------------------- CONTENTS III and ADS : INARTICLE 3 ----------------------- */}
                  {_article?.content_III && (
                    <div id='paragraph-3' className='default-inread'>
                      {_article?.is_show_ads && isExistAds(ads, KEY_ADS_POSITION.INARTICLE_3) && <DynamicAds position={KEY_ADS_POSITION.INARTICLE_3} data={ads} />}
                      <div className='content-detail' dangerouslySetInnerHTML={{ __html: _article?.content_III }} />
                      {_article?.is_show_ads && getAffiliate(3, dataAffiliate)}
                    </div>
                  )}
                  <div></div>

                  {/* ------------------------------ solrRelated ------------------------------ */}
                  <div className='d-block d-lg-none col-12 col-lg-12 block-readmore-content mt-5'>
                    {!isEmpty(related) && (
                      <div className='overflow-hidden'>
                        <Carousels slideShow={2} vertical={true} autoplaySpeed={1500}>
                          {related.slice(0, 5).map(
                            (item, index) => (
                              // <div key={index} className='solr-card-h'>
                              <CardH
                                key={index}
                                data={item}
                                path={item.link}
                                //! DATALAYER
                                eventDataLayer={{
                                  type: DATALAYER_TYPE.TRACK_POSITION,
                                  router: router?.pathname,
                                  section: 'ข่าวที่เกี่ยวข้อง',
                                  position: isDesktop ? 'ใต้:แชร์ข่าว' : 'ใต้:เนื้อหาที่-3',
                                  data: {
                                    block: '1',
                                    title: item?.title,
                                    heading: 'ข่าวที่เกี่ยวข้อง',
                                    carousel: index + 1
                                  }
                                }}
                              />
                            )
                            // {/* </div> */}
                          )}
                        </Carousels>
                      </div>
                    )}
                  </div>
                  {/* ---------------------------------- ADD LINE ------------------------------- */}
                  <LineLink lineContent={lineContent} />

                  {/* ------------------------------ lastContents tags: ขอบสนาม ------------------------------ */}
                  {!isEmpty(articleSports) && (
                    <div className='d-lg-block d-none ruwai-chiwa'>
                      <div className='heading-ruwai-chiwa'>
                        <h2>
                          {' '}
                          คนบ้ากีฬา <span> หามาให้ดู </span>
                        </h2>
                      </div>
                      <div className='row'>
                        {articleSports.slice(0, 4).map((item, index) => (
                          <div key={index} className='col-6 ruwai-chiwa-card-h'>
                            <CardH
                              excerpt={2}
                              showCountview={true}
                              showTime={true}
                              data={item}
                              path={item?.link}
                              //! DATALAYER
                              eventDataLayer={{
                                type: DATALAYER_TYPE.TRACK_POSITION,
                                router: router?.pathname,
                                section: 'คนบ้ากีฬา หามาให้ดู',
                                position: 'ใต้:เพื่มเพื่อน',
                                data: {
                                  block: '1',
                                  title: item?.title,
                                  heading: 'คนบ้ากีฬา หามาให้ดู',
                                  index: index + 1
                                }
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ------------------------------ READ_COMPLETE ------------------------ */}
                  <div id='read-complete' className='content-tags'>
                    {!isEmpty(_article?.tags) && <span>แท็กที่เกี่ยวข้อง</span>}
                    {!isEmpty(_article?.tags) &&
                      _article.tags.map((item, index) => (
                        <a
                          key={index}
                          title={item}
                          href={`/tags/${item}`}
                          //! DATALAYER
                          onClick={() =>
                            sendDataLayerGlobal({
                              type: DATALAYER_TYPE.TRACK_POSITION,
                              router: router?.pathname,
                              section: 'แท็กที่เกี่ยวข้อง',
                              position: isDesktop ? 'ใต้:ข่าวที่เกี่ยวข้อง' : 'ใต้:เพิ่มเพื่อน',
                              data: {
                                block: '1',
                                title: item,
                                heading: 'แท็กที่เกี่ยวข้อง',
                                index: index + 1
                              }
                            })
                          }
                        >
                          {item}
                        </a>
                      ))}

                    {isExistAds(ads, KEY_ADS_POSITION.POSTARTICLE) && (
                      <div>
                        <MediaQuery maxWidth={768}>
                          <DynamicAds position={KEY_ADS_POSITION.POSTARTICLE} data={ads} />
                        </MediaQuery>
                      </div>
                    )}

                    <div className='section-sharelink --mobile'>
                      <div className='mt-4'>
                        <WidgetSocialShare
                          articleLink={article?.link}
                          //! DATALAYER
                          eventDataLayer={{
                            type: DATALAYER_TYPE.TRACK_POSITION,
                            router: router?.pathname,
                            section: 'แชร์ข่าว',
                            position: 'ใต้:แท็กที่เกี่ยวข้อง',
                            data: {
                              block: '1',
                              heading: 'แชร์ข่าว'
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Start Read more ++ --------------------------------------------------------- */}

                  <div className='row d-lg-block d-none'>
                    {!isEmpty(related) && (
                      <Carousels slideShow={3}>
                        {related?.map((blockItem, index) => (
                          <div className='card-v-carousels'>
                            <CardV
                              key={index}
                              data={blockItem}
                              path={blockItem.link}
                              //! DATALAYER
                              eventDataLayer={{
                                type: DATALAYER_TYPE.TRACK_POSITION,
                                router: router?.pathname,
                                section: 'ข่าวที่เกี่ยวข้อง',
                                position: 'ใต้:แชร์ข่าว',
                                data: {
                                  block: '1',
                                  title: blockItem?.title,
                                  heading: 'ข่าวที่เกี่ยวข้อง',
                                  carousel: index + 1
                                }
                              }}
                            />
                          </div>
                        ))}
                      </Carousels>
                    )}
                  </div>
                  {/* ------------------------------ solrRelated-mobile ------------------------------ */}
                  {!isEmpty(solrRelated) && (
                    <div className='d-lg-none d-block mobile-solr-item mt-3'>
                      <Carousels slideShow={1}>
                        {solrRelated.slice(0, 5).map((item, index) => (
                          <div className='card-v-solr-mobile'>
                            <CardV key={index} data={item} path={item.link} external={true} />
                          </div>
                        ))}
                      </Carousels>
                    </div>
                  )}
                  {/* --------------------------- ADS : Native (Infinities) ----------------------------------- */}
                  {article?.is_show_ads && <AdsNative data={dataAdsNative} isShowAds={article?.is_show_ads} />}

                  {/* ------------------------ Cookie ---------------------- */}
                  <div id='cookie' className='displayUnset'>
                    <div className='ads-sticky-overflow'>
                      {/* ----------------------------- ADS : POSTARTICE -------------------------- */}
                      <MediaQuery minWidth={481}>
                        {_article?.is_show_ads && isExistAds(ads, KEY_ADS_POSITION.POSTARTICLE) && <DynamicAds position={KEY_ADS_POSITION.POSTARTICLE} data={ads} />}
                      </MediaQuery>
                      {/* {IS_SHOW_COOKIE === 'true' && (
                        <div className='displayC'>
                          <WidgetCookie />
                        </div>
                      )} */}
                      {/* ----------------------------- ADS : POSTARTICE -------------------------- */}
                    </div>
                  </div>
                </div>
              </div>

              {/* ---------------------------- BLOCKSIDE : START --------------------------- */}
              <div className='col-12 col-lg-4 d-none d-lg-block position-relative'>
                <StickyBox offsetTop={170} offsetBottom={50}>
                  {/* ----------------------------- ADS : STICKY - 1 ---------------------------- */}
                  <div className='side-article'>
                    {/* ----------------------------- ADS : STICKY-1 -------------------------- */}
                    {_article.is_show_ads && isExistAds(ads, KEY_ADS_POSITION.STICKY_1) && (
                      <div className='default-sticky'>
                        <DynamicAds position={KEY_ADS_POSITION.STICKY_1} data={ads} />{' '}
                      </div>
                    )}

                    {!isEmpty(solrRelated) && (
                      <div className='mb-30'>
                        <h2 className='heading mt-15'>ข่าวที่น่าสนใจ</h2>
                        <div className='row'>
                          {solrRelated.slice(0, 5).map((item, index) => (
                            <div key={index} className='col-12   solr-card-h'>
                              <CardH
                                data={item}
                                path={item?.link}
                                external={true}
                                //! DATALAYER
                                eventDataLayer={{
                                  type: DATALAYER_TYPE.TRACK_POSITION,
                                  router: router?.pathname,
                                  section: 'ข่าวที่น่าสนใจ',
                                  position: 'ใต้:โฆษณาตำแหน่ง billboard',
                                  data: {
                                    block: '2',
                                    title: item?.title,
                                    heading: 'ข่าวที่น่าสนใจ',
                                    index: index + 1
                                  }
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div></div>

                    {/* ----------------------------- ADS : STICKY - 2 ---------------------------- */}
                    {_article.is_show_ads && isExistAds(ads, KEY_ADS_POSITION.STICKY_2) && (
                      <div className='default-sticky'>
                        <DynamicAds position={KEY_ADS_POSITION.STICKY_2} data={ads} />{' '}
                      </div>
                    )}

                    {!isEmpty(lastNews) && (
                      <>
                        <div className='mb-30'>
                          <h2 className='heading'>ข่าวล่าสุด</h2>
                          <div className='row'>
                            {lastNews.slice(0, 2).map((item, index) => (
                              <div className='col-12 mt-15 mb-15 card-v' key={index}>
                                <CardV
                                  data={item}
                                  path={item?.link}
                                  byline={true}
                                  //! DATALAYER
                                  eventDataLayer={{
                                    type: DATALAYER_TYPE.TRACK_POSITION,
                                    router: router?.pathname,
                                    section: 'ข่าวล่าสุด',
                                    position: 'ใต้:โฆษณาตำแหน่ง billboard',
                                    data: {
                                      block: '2',
                                      title: item?.title,
                                      heading: 'ข่าวล่าสุด',
                                      index: index + 1
                                    }
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    {/* ----------------------------- ADS : STICKY-3 -------------------------- */}
                    {_article.is_show_ads && isExistAds(ads, KEY_ADS_POSITION.STICKY_3) && (
                      <div className='default-sticky'>
                        <DynamicAds position={KEY_ADS_POSITION.STICKY_3} data={ads} />
                      </div>
                    )}
                  </div>
                </StickyBox>
              </div>
              {/* ---------------------------- BLOCKSIDE : END --------------------------- */}
            </div>
          </div>
        </div>

        {/* ----------------------------------- CONTENTS : End ---------------------------------- */}
      </DetailWrapper>
      {/* ------------------------------ MAIN : END ------------------------------ */}
    </>
  );
};

const DetailWrapper = styled.div`
  .block-readmore-content {
    max-width: 90%;
    width: 100%;
    margin: auto;
    margin-top: 10px;
  }
  .ruwai-chiwa {
    .heading-ruwai-chiwa {
      display: flex;
      align-items: center;
      width: 100%;
      margin-top: 30px;
      border-bottom: 1px solid;
      h2,
      span {
        font-family: 'Sarabun', sans-serif;
        padding-bottom: 5px;
        font-size: 20px !important;
        line-height: 1.75;
        margin: 0;
        color: rgba(0, 170, 44);
      }
      span {
        font-weight: 900;
        margin-left: -3px;
        color: rgba(0, 2, 28, 9.5);
      }
    }
    .related-card-h:nth-child(5),
    .related-card-h:nth-child(6) {
      .card-h {
        border: none;
      }
    }
    .card-h {
      box-shadow: unset;
      border-radius: unset;
      h3 {
        padding: 0px !important;
        font-size: 16px !important;
        font-family: 'Sarabun', sans-serif;
        line-height: 1.75;
        font-weight: 400;
      }
    }
    .ruwai-chiwa-card-h {
      .img-scale-animate {
        border-radius: 3px;
        border: unset !important;
        box-shadow: unset !important;
      }
      .resolution-image {
        position: relative !important;
        @media (max-width: 1024px) and (min-width: 767px) {
        }
      }
    }
  }
  .side-article {
    .card-v {
      padding: 0px;
    }

    .heading {
      margin-top: 20px;
      margin-left: -3px;
    }
  }
  padding-top: 30px;
  .card-v-carousels {
    padding: 8px;
  }
  .card-v-solr-mobile {
    padding: 15px;
  }
  /* twitter */
  .twitter-tweet {
    margin: auto;
  }
  .section-readmore {
    background: linear-gradient(180deg, rgba(238, 243, 246, 1) 95%, rgba(255, 255, 255, 1) 100%);
    padding-bottom: 30px;
    .block-readmore-content {
      .post-info {
        .byline {
          @media (max-width: 475px) {
            padding-bottom: 5px;
          }
        }
      }
    }
  }
  .article-read-more:not(:nth-child(1)) {
    margin-top: 45px;
  }
  .article-read-more:not(:last-child) {
    margin-bottom: 0px;
  }

  .article-read-more {
    border-radius: 3px;
    background-color: #fff;
    padding: 15px 15px 20px 15px;
    /* margin-top:20px; */
    margin-bottom: 20px;
    min-height: 300px;
    max-height: 900px;
    position: relative;
    overflow: hidden;
    .button-readmore {
      position: absolute;
      bottom: 40px;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 2;
      background-color: #00ff57;
      border: 2px solid #00ff57;
      border-radius: 2px;
      padding: 5px 20px;
      cursor: pointer;
      transition: all 0.5s;
      &:hover {
        background-color: rgba(1, 11, 43, 0.95);
        border: 2px solid #00ff57;
        &:hover {
          a {
            color: #00ff57;
          }
        }
      }
      a {
        transition: all 0.5s;
        color: black;
        font-size: 16px;
        font-weight: 500;
      }
    }
    .fade-content-readmore {
      position: absolute;
      height: 60%;
      width: 100%;
      background-image: linear-gradient(to top, #fffffff4, #ffffff00);
      bottom: 0;
      z-index: 1;
    }
    .breadcrumb-area-readmore {
      .breadcrumb-title-readmore {
        h3 {
          margin: 0;
          font-size: 28px;
        }
        @media (max-width: 768px) {
          h3 {
            font-size: 24px;
          }
        }
      }
    }

    .content-feature-image-readmore {
      .resolution-image {
        border-radius: 3px;
      }
      .embed-responsive {
        iframe {
          width: 100%;
          height: 400px;
          @media (max-width: 500px) {
            height: 220px;
          }
        }
      }
    }
    .content-blurb {
      font-size: 18px;
    }
  }
  .breadcrumb-area-video {
    .breadcrumb-title-video {
      .category {
        margin: 0 0 15px 0;
      }
      h1 {
        @media (max-width: 1024px) {
          font-size: 28px;
        }
        @media (max-width: 480px) {
          font-size: 26px;
        }
      }
    }
  }

  .post-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 8px 0px 18px 0;
    .dateUpdate {
      display: flex;
      list-style: none;
      padding: 0;
      margin: 0;
      /* margin: 15px 0 25px 0; */
      li {
        svg,
        span {
          vertical-align: middle;
          color: rgba(0, 2, 28, 0.75);
          font-size: 14px;
          @media (max-width: 475px) {
            font-size: 12px;
          }
        }
        span {
          margin-left: 10px;
        }
      }
      li + li {
        padding-left: 10px;
      }
    }
    .byline {
      /* margin: 15px 0 25px 0; */
      svg {
        margin-bottom: 2px;
      }
      svg,
      span {
        vertical-align: middle;
        text-transform: uppercase;
        color: rgba(0, 2, 28, 0.75);
        font-size: 14px;
      }
    }
    @media (max-width: 475px) {
      margin: 8px 0px 8px 0;
      display: block;
      .byline {
        padding: 5px 0;
        span {
          font-size: 12px;
        }
      }
    }
  }
  .content-blurb {
    color: rgba(0, 0, 28, 0.95);
    font-size: 22px;
    line-height: 1.75;
    @media (max-width: 768px) {
      border-bottom: 1px solid rgba(244, 244, 244);
      padding-bottom: 10px;
      font-size: 20px;
      margin: 15px 0 15px 0;
    }
  }

  .article-area {
    padding-top: 15px;
    .accept-cookie {
      @media (min-width: 769px) {
        margin-bottom: 30px !important;
      }
    }
  }

  .content-detail {
    font-family: 'Sarabun', sans-serif;
    font-size: 16px;
    line-height: 1.75;
    font-weight: 300;
    letter-spacing: 0.25px;
    word-break: break-word;
    font-display: swap;

    h2 {
      font-size: 18px;
      display: block;
      margin: 10px 0;
      font-size: 18px;
      font-weight: 500;
      color: #000;
      width: 95%;
      line-height: 1.8;
      border-left: 8px solid rgba(0, 170, 44);
      background: linear-gradient(90deg, #f8f8f8 85%, white 100%);
      padding: 15px 15px 15px 35px;
      border-radius: 4px;
    }

    pre {
      white-space: break-spaces;
    }

    strong,
    span {
      font-weight: 700;
    }
    img {
      max-width: 100%;
      margin: 20px auto;
      height: auto !important;
      width: 100% !important;
    }

    p {
      a {
        color: #00b83d;
        font-weight: 700;
      }
    }
    a {
      color: rgba(1, 11, 43, 0.95);

      &:hover {
        color: #008f30;
      }
    }
    iframe {
      position: relative !important;
      margin: 10px auto !important;
      max-width: 100%;
      display: flex;
      min-height: 400px;
      @media (max-width: 475px) {
        min-height: unset;
      }
    }

    ul {
      li {
        list-style-type: circle;
        display: list-item;
        a {
          font-family: 'Prompt', sans-serif;
          letter-spacing: 0.2px;
          font-display: swap;
        }
      }
    }
    blockquote {
      @media (max-width: 480px) {
        margin: 0;
      }
    }

    blockquote > p,
    blockquote > span,
    blockquote > strong {
      background: rgb(232 255 232 / 60%);
      padding: 30px;
      margin: 10px;
      border-left: 3px solid #0f0;
      @media (max-width: 768px) {
        padding: 15px 20px;
      }
    }

    picture {
      display: inline-block;
      span {
        width: 100%;
        display: block;
        position: relative;
        text-align: center;
        font-size: 13px;
        font-weight: 300;
        line-height: 1.6;
        color: rgb(117, 117, 117);
        padding: 10px;
        margin: -5px auto 15px 0px;
      }
    }
    .image-caption {
      font-weight: 400;
      font-size: 13px !important;
      font-style: italic;
      letter-spacing: 0.25px;
    }
    .affaliate-image {
      width: 80% !important;
    }

    // iframe dailymotion
    div[style='position:relative;padding-bottom:56.25%;height:0;overflow:hidden;'] > iframe {
      display: block;
      position: relative !important;
      margin: 20px auto !important;
      max-width: 100% !important;
      height: 480px;
      @media (max-width: 767px) {
        height: 190px !important;
      }
    }
    div[style='position:relative;padding-bottom:56.25%;height:0;overflow:hidden;'] {
      height: 480px !important;
      @media (max-width: 767px) {
        height: 200px !important;
      }
    }
    // iframe dailymotion end
  }

  //START TAG หน้าอ่าน ********************************
  .content-tags {
    font-size: 14px;
    margin-top: 20px;
    margin-bottom: 20px;
    span {
      border-radius: 2px;
      padding: 7px 10px;
      background-color: rgba(1, 11, 43, 0.95);
      color: #ffffff;
      margin-right: 5px;
    }
    a {
      display: inline-flex;
      border: 1px solid #00b83d;
      border-radius: 2px;
      padding: 4px 8px;
      color: #444444;
      margin: 5px 5px 0 0;
      @media (min-width: 475px) {
        margin: 10px 5px 0 0;
      }
      transition: all 0.5s ease-out;
      &:hover {
        background-color: #00ff57;
      }
    }
  }
  //END TAG หน้าอ่าน ********************************

  /* start share */
  .section-sharelink {
    .article-share {
      .social {
        display: flex;
        list-style-type: none;
        align-items: center;
        padding: 0;
        @media (max-width: 768px) {
          justify-content: center;
        }
        li:not(:first-child) {
          padding-left: 5px;
        }
      }
    }
  }
  /* end share */
  .solr-card-h {
    padding-right: 15px;
  }

  .solr-card-h,
  .related-card-h {
    padding-left: 3px;
    .resolution-image {
      @media (max-width: 1024px) and (min-width: 767px) {
        position: absolute !important;
      }
    }
  }
  .card-h-readmore {
    .readmore {
      background-color: #fff;
      border-radius: 3px;
    }
  }
  // accept cookie
  .displayUnset {
    display: unset;
    padding: 0;
    position: sticky;
    bottom: 0;
  }

  /* start head Video only */
  .content-feature-image-video {
    .embed-responsive {
      iframe {
        border: none;
        border-radius: 3px;
        width: 100%;
        height: 400px;
        @media (max-width: 500px) {
          height: 220px;
        }
      }
    }
  }
  /* end head Video only */
`;

export default ReadFullVideo;
